import React from "react";
import { Link } from "react-router-dom";
import {
  FiShield,
  FiLock,
  FiMapPin,
  FiCamera,
  FiMessageSquare,
  FiBell,
  FiUserCheck,
  FiGlobe,
  FiMail,
  FiCheckCircle,
  FiDatabase,
  FiFileText,
  FiArrowLeft,
  FiExternalLink,
  FiInfo,
  FiKey,
} from "react-icons/fi";

export default function PrivacyPolicy() {
  const lastUpdated = "October 10, 2026";

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased selection:bg-[#4a90e2]/20 selection:text-[#0f2a44]">
      {/* ── Top Navigation Bar ─────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-[#0f2a44]/95 backdrop-blur-md border-b border-white/10 text-white shadow-lg shadow-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/images/logo.png"
              alt="Sahvia Consultants Logo"
              className="h-8 sm:h-9 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <span className="text-sm font-bold tracking-wide text-white block leading-tight">
                SAHVIA HRMS
              </span>
              <span className="text-[10px] tracking-wider uppercase text-blue-200/70 font-semibold block">
                Sahvia Consultants
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-400/30">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Official Policy Active
            </span>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[#4a90e2] to-[#6bb3ff] hover:opacity-95 shadow-md shadow-blue-500/25 active:scale-95 transition-all"
            >
              <FiArrowLeft className="w-4 h-4" />
              <span>Portal Login</span>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero Banner Section ────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0f2a44] via-[#123354] to-[#18426d] text-white py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-b border-blue-900/30">
        {/* Subtle background decoration glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-blue-500/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-4xl mx-auto text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs sm:text-sm font-medium text-blue-100 backdrop-blur-md shadow-sm">
            <FiShield className="text-blue-300 w-4 h-4" />
            <span>Enterprise Privacy & Regulatory Transparency</span>
          </div>

          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Privacy Policy
          </h1>

          <p className="text-sm sm:text-base text-blue-100/90 max-w-2xl mx-auto leading-relaxed">
            Comprehensive disclosure of data handling, security, and privacy governance for the{" "}
            <strong className="text-white">Sahvia HRMS Mobile Application & Web Portals</strong> operated by{" "}
            <strong className="text-white">Sahvia Consultants</strong>.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs text-blue-200/80 font-medium">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-400" />
              Last Revised: {lastUpdated}
            </span>
            <span className="hidden sm:inline text-blue-300/40">•</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              Entity: Sahvia Consultants
            </span>
            <span className="hidden sm:inline text-blue-300/40">•</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-cyan-400" />
              Applicable to: Mobile & Web
            </span>
          </div>
        </div>
      </section>

      {/* ── Quick Jump Navigation Anchor Strip ─────────────────────────── */}
      <div className="sticky top-16 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm py-2 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-2 overflow-x-auto no-scrollbar text-xs font-semibold text-slate-600">
          <span className="text-slate-400 uppercase tracking-wider text-[11px] font-bold mr-1 flex-shrink-0">
            Quick Jump:
          </span>
          <button
            onClick={() => scrollToSection("purpose")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            1. Purpose
          </button>
          <button
            onClick={() => scrollToSection("collection")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            2. Collected Data
          </button>
          <button
            onClick={() => scrollToSection("usage")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            3. Data Usage
          </button>
          <button
            onClick={() => scrollToSection("sharing")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            4. No Third-Party Ads
          </button>
          <button
            onClick={() => scrollToSection("security")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            5. Security
          </button>
          <button
            onClick={() => scrollToSection("deletion")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            6. Retention & Deletion
          </button>
          <button
            onClick={() => scrollToSection("contact")}
            className="px-3 py-1.5 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition flex-shrink-0"
          >
            7. Contact
          </button>
        </div>
      </div>

      {/* ── Main Content Area ──────────────────────────────────────────── */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-10">
        {/* Organization Scope Card */}
        <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50/70 border border-blue-200/70 text-slate-800">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <FiInfo className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <h2 className="text-base font-bold text-slate-900">
                Scope & Enterprise Governance Notice
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                Welcome to <strong>Sahvia HRMS</strong>, an enterprise workforce management platform developed and maintained by <strong>Sahvia Consultants</strong> (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). This policy governs the processing of information gathered through our native mobile applications on Android/iOS and connected web platforms accessible at{" "}
                <a
                  href="https://sahviavn.com"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline font-medium hover:text-blue-800 inline-flex items-center gap-1"
                >
                  https://sahviavn.com <FiExternalLink className="w-3 h-3 inline" />
                </a>.
              </p>
            </div>
          </div>
        </div>

        {/* 1. Purpose of the Application */}
        <section id="purpose" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              01
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Purpose of the Application
              </h3>
              <p className="text-xs text-slate-500">Authorized workforce operations only</p>
            </div>
          </div>

          <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
            <p>
              Sahvia HRMS is a workplace productivity and employee self-service platform designed exclusively for authorized employees, managers, and administrative personnel of <strong>Sahvia Consultants</strong>.
            </p>
            <p>
              All user accounts are strictly provisioned, managed, and controlled by your organization&apos;s Human Resources and Administrative leadership. The app is not a public commercial social network; its sole purpose is enabling streamlined operational workflows, attendance logging, leave approvals, and professional task tracking.
            </p>
          </div>
        </section>

        {/* 2. Information We Collect */}
        <section id="collection" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-6">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              02
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Information We Collect & Permissions Used
              </h3>
              <p className="text-xs text-slate-500">Transparent breakdown of every requested data point</p>
            </div>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed">
            To provide accurate timesheet generation, role validation, and real-time team collaboration, Sahvia HRMS requests only the minimum necessary permissions:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Item 1: Personal Data */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors space-y-2">
              <div className="flex items-center gap-2.5 text-[#0f2a44] font-semibold text-sm">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <FiUserCheck className="w-4 h-4" />
                </div>
                <span>Personal & Employee Profile Data</span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Full name, unique employee ID number, official corporate email, assigned department, designation, and reporting manager. Provisioned by your employer to establish your organizational identity.
              </p>
            </div>

            {/* Item 2: Geolocation */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors space-y-2">
              <div className="flex items-center gap-2.5 text-[#0f2a44] font-semibold text-sm">
                <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                  <FiMapPin className="w-4 h-4" />
                </div>
                <span>Location Data (Punch-In Verification Only)</span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Exact GPS coordinates are queried <strong>strictly at the specific second</strong> when you tap to Punch In or Punch Out. This confirms attendance at authorized company offices or client sites. We <strong>never perform continuous background location tracking</strong>.
              </p>
            </div>

            {/* Item 3: Camera & Photos */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors space-y-2">
              <div className="flex items-center gap-2.5 text-[#0f2a44] font-semibold text-sm">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                  <FiCamera className="w-4 h-4" />
                </div>
                <span>Camera & Storage Access</span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Invoked solely when you intentionally choose to capture or attach a profile photo, upload reimbursable expense bills, or share project documents in workspace conversations.
              </p>
            </div>

            {/* Item 4: Workplace Chat */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors space-y-2">
              <div className="flex items-center gap-2.5 text-[#0f2a44] font-semibold text-sm">
                <div className="p-2 rounded-lg bg-sky-100 text-sky-600">
                  <FiMessageSquare className="w-4 h-4" />
                </div>
                <span>In-App Messages & Attachments</span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Work-related text conversations and shared files transmitted within the corporate chat channel are stored securely on our enterprise servers for operational audit and collaboration.
              </p>
            </div>

            {/* Item 5: Notifications */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors space-y-2">
              <div className="flex items-center gap-2.5 text-[#0f2a44] font-semibold text-sm">
                <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                  <FiBell className="w-4 h-4" />
                </div>
                <span>Push Notification Tokens</span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Firebase Cloud Messaging (FCM) and Expo push tokens are assigned to your device hardware exclusively for urgent broadcast alerts, leave status approvals, and incoming message pings.
              </p>
            </div>

            {/* Item 6: Biometrics */}
            <div className="p-4 sm:p-5 rounded-xl bg-slate-50 border border-slate-200 hover:border-blue-300 transition-colors space-y-2">
              <div className="flex items-center gap-2.5 text-[#0f2a44] font-semibold text-sm">
                <div className="p-2 rounded-lg bg-rose-100 text-rose-600">
                  <FiKey className="w-4 h-4" />
                </div>
                <span>Hardware Biometrics (Face / Fingerprint)</span>
              </div>
              <p className="text-xs sm:text-[13px] text-slate-600 leading-relaxed">
                Biometric unlock relies strictly on the native Android BiometricPrompt architecture. <strong>Your biometric credentials remain securely encapsulated within your device hardware</strong> and are never transmitted to or held on our servers.
              </p>
            </div>
          </div>
        </section>

        {/* 3. How We Use Your Information */}
        <section id="usage" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              03
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                How We Use Your Information
              </h3>
              <p className="text-xs text-slate-500">Operational, compliance, and employment purposes</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs sm:text-sm text-slate-700">
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Authenticating authorized workplace users and preventing unauthorized portal intrusion.</span>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Verifying shift attendance, office check-ins, and generating monthly payroll timesheets.</span>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Processing paid time-off (PTO), sick leaves, and manager approval chains.</span>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Tracking project deliverables, daily work logs, and performance metrics.</span>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Facilitating internal collaboration through real-time team messaging.</span>
            </div>
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-slate-50 border border-slate-100">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span>Preventing proxy check-ins, buddy-punching, and fraudulent attendance claims.</span>
            </div>
          </div>
        </section>

        {/* 4. Zero Advertising & Third-Party Disclosure */}
        <section id="sharing" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 text-emerald-700 flex items-center justify-center font-bold text-sm">
              04
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Zero Commercial Advertising & Third-Party Sharing
              </h3>
              <p className="text-xs text-slate-500">Strictly no data monetization or broker sales</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-emerald-50/80 border border-emerald-200 text-emerald-900 text-xs sm:text-sm font-semibold flex items-center gap-3">
            <FiShield className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span>
              We do not sell, rent, license, commercialize, or share your personal data with third-party advertising networks, data brokers, or marketing platforms.
            </span>
          </div>

          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            Information is accessed solely by designated organizational supervisors, human resources teams, and system administrators strictly for legitimate employment governance. External disclosure occurs only when required by statutory labor regulations, valid court subpoenas, or legal mandates.
          </p>
        </section>

        {/* 5. Enterprise Security & Encryption */}
        <section id="security" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              05
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Security & Encryption Standards
              </h3>
              <p className="text-xs text-slate-500">Enterprise data safeguards</p>
            </div>
          </div>

          <div className="space-y-3 text-xs sm:text-sm text-slate-700">
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-3">
              <FiLock className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <strong className="text-slate-900 block mb-0.5">TLS / HTTPS Encryption in Transit</strong>
                <span>All network communications between mobile clients and API servers are strictly protected using modern TLS 1.3/HTTPS and secure WebSocket (WSS) protocol layers.</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-3">
              <FiDatabase className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" />
              <div>
                <strong className="text-slate-900 block mb-0.5">Role-Based Access Control (RBAC)</strong>
                <span>Data visibility is partitioned into rigorous role tiers (Employee, Manager, Administrator) so employees only access information relevant to their assigned duties.</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-3">
              <FiShield className="w-4 h-4 text-emerald-600 mt-1 flex-shrink-0" />
              <div>
                <strong className="text-slate-900 block mb-0.5">Isolated Enterprise Hosting</strong>
                <span>HRMS databases reside on secured cloud servers equipped with automated snapshots, access logging, and perimeter firewall monitoring.</span>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Data Retention & Account Deletion */}
        <section id="deletion" className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-4">
          <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              06
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Data Retention & Account Deletion Requests
              </h3>
              <p className="text-xs text-slate-500">Employee lifecycle and data deletion protocols</p>
            </div>
          </div>

          <div className="space-y-3 text-xs sm:text-sm text-slate-700 leading-relaxed">
            <p>
              Corporate records, timesheets, and project logs are maintained throughout your active tenure with <strong>Sahvia Consultants</strong> in conformance with standard statutory payroll and employment record laws.
            </p>
            <p>
              When an employee departs the organization, their credentials are deactivated immediately by the corporate administrator.
            </p>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
              <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                <FiFileText className="text-blue-600" />
                How to Request Account Deactivation or Data Purge:
              </h4>
              <p className="text-slate-600">
                To initiate account closure, request removal of optional profile data, or inquire about archived records, employees may contact their HR department manager or send a written inquiry to our support desk at{" "}
                <a
                  href="mailto:support@sahviavn.com"
                  className="text-blue-600 font-semibold hover:underline"
                >
                  support@sahviavn.com
                </a>. Requests are validated against corporate statutory record retention requirements and processed within 30 business days.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Children's Privacy */}
        <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-3">
          <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              07
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Children&apos;s Privacy
              </h3>
              <p className="text-xs text-slate-500">Workplace application age requirement</p>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            Sahvia HRMS is exclusively tailored for employed adult workforce personnel (minimum 18 years of age). We do not knowingly solicit, collect, or store personal information from individuals under the age of 18.
          </p>
        </section>

        {/* 8. Policy Updates */}
        <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8 space-y-3">
          <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
            <div className="w-9 h-9 rounded-lg bg-[#4a90e2]/15 text-[#2a6edc] flex items-center justify-center font-bold text-sm">
              08
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                Policy Updates & Revisions
              </h3>
              <p className="text-xs text-slate-500">Ongoing compliance maintenance</p>
            </div>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
            We may revise this Privacy Policy periodically to reflect technological improvements, app feature additions, or regulatory updates. Any changes will be published directly here with the updated revision date.
          </p>
        </section>

        {/* 9. Official Contact Details */}
        <section id="contact" className="bg-gradient-to-br from-[#0f2a44] to-[#1c3f66] rounded-2xl border border-blue-900 text-white p-6 sm:p-8 shadow-xl space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-white/10">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-300 flex items-center justify-center font-bold">
              <FiMail className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">
                Contact & Regulatory Inquiries
              </h3>
              <p className="text-xs text-blue-200/70">Official corporate contact channels</p>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-blue-100/90 leading-relaxed">
            If you have questions, feedback, or requests regarding this Privacy Policy or your enterprise personal data, please reach out to our team:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm space-y-1">
              <span className="text-[11px] font-semibold text-blue-300 uppercase tracking-wider block">
                Organization
              </span>
              <p className="text-sm font-bold text-white">
                Sahvia Consultants
              </p>
              <p className="text-xs text-blue-200/60">Enterprise Management</p>
            </div>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm space-y-1">
              <span className="text-[11px] font-semibold text-blue-300 uppercase tracking-wider block">
                Official Web Portal
              </span>
              <a
                href="https://sahviavn.com"
                target="_blank"
                rel="noreferrer"
                className="text-sm font-bold text-cyan-300 hover:underline flex items-center gap-1"
              >
                <FiGlobe className="w-3.5 h-3.5" />
                sahviavn.com
              </a>
              <p className="text-xs text-blue-200/60">Enterprise Gateway</p>
            </div>

            <div className="p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm space-y-1">
              <span className="text-[11px] font-semibold text-blue-300 uppercase tracking-wider block">
                Corporate Support
              </span>
              <a
                href="mailto:support@sahviavn.com"
                className="text-sm font-bold text-cyan-300 hover:underline flex items-center gap-1"
              >
                <FiMail className="w-3.5 h-3.5" />
                support@sahviavn.com
              </a>
              <p className="text-xs text-blue-200/60">Data Governance Desk</p>
            </div>
          </div>
        </section>

        {/* Back to Login Bottom Action */}
        <div className="text-center pt-4">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4a90e2] to-[#6bb3ff] hover:opacity-95 shadow-lg shadow-blue-500/20 active:scale-95 transition-all"
          >
            <FiArrowLeft className="w-4 h-4" />
            <span>Return to HRMS Login Portal</span>
          </Link>
        </div>
      </main>

      {/* ── Enterprise Footer ──────────────────────────────────────────── */}
      <footer className="bg-[#0f2a44] border-t border-white/10 text-slate-300 py-8 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Sahvia Consultants Logo" className="h-6 w-auto" />
            <span>&copy; {new Date().getFullYear()} Sahvia Consultants. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-4 text-blue-200/80">
            <Link to="/login" className="hover:text-white transition">
              Portal Login
            </Link>
            <span>•</span>
            <a
              href="https://sahviavn.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white transition"
            >
              Company Website
            </a>
            <span>•</span>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="hover:text-white transition cursor-pointer"
            >
              Top ↑
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
